import { useState, useEffect } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const BenefitsAdminTab = () => {
  const [overview, setOverview] = useState('');
  const [plans, setPlans] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    hrAPI.getBenefits().then((res) => {
      setOverview(res.data.overview || '');
      setPlans(res.data.plans || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await hrAPI.updateBenefits({ overview, plans });
      toast.success('Benefits information published');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updatePlan = (i, key, value) => {
    setPlans(plans.map((p, idx) => idx === i ? { ...p, [key]: value } : p));
  };

  if (!loaded) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6" data-testid="hr-admin-benefits-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg">Benefits Overview</CardTitle></CardHeader>
        <CardContent>
          <Label className="text-slate-700">Overview text shown to all employees</Label>
          <Textarea value={overview} onChange={(e) => setOverview(e.target.value)} rows={5}
            placeholder="Describe your company's benefits: health insurance, dental, retirement plans, etc."
            className="bg-white border-slate-200 mt-1" data-testid="hr-admin-benefits-overview-input" />
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-slate-800 text-lg">Benefit Plans</CardTitle>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setPlans([...plans, { name: '', type: 'health', provider: '', details: '' }])} data-testid="hr-admin-add-plan-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Plan
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {plans.length === 0 && <p className="text-sm text-slate-400">No plans added. Click "Add Plan" to list health, dental, vision, retirement plans etc.</p>}
          {plans.map((p, i) => (
            <div key={i} className="p-4 rounded-xl border border-slate-200 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-700">Plan Name</Label>
                  <Input value={p.name} onChange={(e) => updatePlan(i, 'name', e.target.value)} placeholder="e.g. Extended Health Plan" className="bg-white border-slate-200 mt-1" data-testid={`hr-admin-plan-name-${i}`} />
                </div>
                <div>
                  <Label className="text-slate-700">Type</Label>
                  <Input value={p.type} onChange={(e) => updatePlan(i, 'type', e.target.value)} placeholder="health / dental / vision / retirement" className="bg-white border-slate-200 mt-1" />
                </div>
                <div>
                  <Label className="text-slate-700">Provider</Label>
                  <Input value={p.provider} onChange={(e) => updatePlan(i, 'provider', e.target.value)} placeholder="e.g. Sun Life" className="bg-white border-slate-200 mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-slate-700">Details</Label>
                <Textarea value={p.details} onChange={(e) => updatePlan(i, 'details', e.target.value)} rows={2} className="bg-white border-slate-200 mt-1" />
              </div>
              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 rounded-full" onClick={() => setPlans(plans.filter((_, idx) => idx !== i))}>
                <Trash2 className="w-4 h-4 mr-1" /> Remove Plan
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" data-testid="hr-admin-benefits-save-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Publish Benefits Info
      </Button>
    </div>
  );
};
